import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { z } from 'zod';
import { rootRoute } from './__root';
import { emailAccountKeys } from '@/features/email-accounts/queries';
import { DetailLoadingState } from '@/features/email-accounts/detail/DetailStates';
import { getEmailAccountOverview } from '@/lib/email-accounts';
import { requireAuth } from '@/lib/route-guards';
import { espAnalyticsSearchSchema } from '@/features/warmup-analytics/search-schema';
import { detailTabSchema } from '@/features/email-accounts/detail/detail-tabs';

// `?section=` is a soft deep-link hint — `.catch(undefined)` degrades a bad
// value to no-scroll. ESP filter keys are merged from the analytics schema
// (not re-declared) so this page's `#section-esp` and the URL can't drift.
// `?tab=` is the active Overview/Activity/Analytics tab (`detailTabSchema`,
// from `detail-tabs.ts` — the same list `DetailTabNav` renders), defaulted to
// `'overview'` where it's consumed (`use-account-detail.ts`), not here.
const detailSearchSchema = z
  .object({
    section: z.enum(['connection', 'dns', 'analytics', 'esp']).optional().catch(undefined),
    // DNS record whose popup opens on arrival (the accounts table's DNS error icon).
    dnsRecord: z
      .enum(['spf', 'dkim', 'dmarc', 'mx', 'domainBlacklist', 'ipBlacklist'])
      .optional()
      .catch(undefined),
    tab: detailTabSchema,
  })
  .merge(espAnalyticsSearchSchema);

export const emailAccountDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/email-accounts/$id',
  validateSearch: detailSearchSchema,
  beforeLoad: requireAuth,
  // The dynamic segment is a string at the URL boundary; we coerce to number
  // once here so `useParams()` in the page can rely on `id: number`.
  parseParams: (params) => ({ id: Number(params.id) }),
  stringifyParams: (params) => ({ id: String(params.id) }),
  // Primes the overview cache; loader errors are swallowed — the page
  // renders its own error/empty/loading states.
  loader: ({ context: { queryClient }, params }) =>
    queryClient
      .ensureQueryData({
        queryKey: emailAccountKeys.overview(params.id),
        queryFn: () => getEmailAccountOverview(params.id),
      })
      .catch(() => undefined),
  // `pendingMs`/`pendingMinMs: 0` — no debounce, no forced display floor;
  // the skeleton shows for exactly as long as the fetch takes.
  pendingComponent: DetailLoadingState,
  pendingMs: 0,
  pendingMinMs: 0,
  component: lazyRouteComponent(
    () => import('@/features/email-accounts/EmailAccountDetailPage'),
    'EmailAccountDetailPage',
  ),
});
